import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Zap } from "lucide-react";

const LABELS = { formation: "Formation", compliance: "Compliance", ip_protection: "IP protection", funding_grants: "Funding & grants", real_estate: "Real estate", estate_planning: "Estate planning", si_software: "SI software", marketplace: "Marketplace", digital_product: "Digital products", other: "Other" };

export function AutoRules() {
    const [data, setData] = useState(null);
    const [rules, setRules] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/admin/si/rules").then((r) => { setData(r.data); setRules(r.data.rules); }).catch(() => {}); }, []);
    if (!rules) return null;

    const toggleCat = (c) => setRules((r) => ({ ...r, categories: r.categories.includes(c) ? r.categories.filter((x) => x !== c) : [...r.categories, c] }));
    const save = async () => {
        setBusy(true);
        try { const { data: d } = await api.put("/admin/si/rules", rules); setRules(d.rules); toast.success("Auto-reply rules saved"); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };

    return (
        <section className="glass-card rounded-2xl p-6" data-testid="si-auto-rules">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2 font-heading text-lg font-bold text-navy"><Zap className="h-5 w-5 text-teal" /> Reception auto-reply rules</div>
                    <p className="mt-1 max-w-2xl text-sm text-slate-500">When enabled, Smarter Reception sends its drafted reply automatically for the categories you allow — only when the AI priority is not "high", and never more than {rules.daily_cap} per day. Every auto-sent email still appears in history.</p>
                </div>
                <label className="flex items-center gap-3 text-sm font-semibold text-navy">
                    <Switch checked={rules.enabled} onCheckedChange={(v) => setRules({ ...rules, enabled: v })} data-testid="si-rules-enabled" /> {rules.enabled ? "Enabled" : "Off"}
                </label>
            </div>
            <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {data.categories.map((c) => (
                    <label key={c} className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm ${rules.categories.includes(c) ? "border-teal/40 bg-teal/5 text-navy" : "border-slate-200 text-slate-600"}`}>
                        <Checkbox checked={rules.categories.includes(c)} onCheckedChange={() => toggleCat(c)} data-testid={`si-rules-cat-${c}`} /> {LABELS[c] || c}
                    </label>
                ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-600">Daily cap
                    <input type="number" min={0} max={25} value={rules.daily_cap} onChange={(e) => setRules({ ...rules, daily_cap: Math.min(25, Math.max(0, +e.target.value || 0)) })} data-testid="si-rules-cap" className="h-9 w-20 rounded-lg border border-slate-200 px-2 text-navy" />
                    <span className="text-xs text-slate-400">(max 25)</span>
                </label>
                <span className="text-sm text-slate-500" data-testid="si-rules-used">Auto-sent today: <b className="text-navy">{data.auto_sent_today}</b></span>
                <Button onClick={save} disabled={busy} data-testid="si-rules-save" className="ml-auto rounded-full bg-navy text-white hover:bg-ocean">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save rules"}</Button>
            </div>
        </section>
    );
}
